import React, { useCallback, useEffect, useState } from 'react';
import { BarChart3, Download, Loader2, AlertCircle } from 'lucide-react';
import { useApi } from '../hooks/useApi';
import { useToast } from '../hooks/useToast';

interface MonthlyData {
  year: number;
  month: number;
  days: Array<{ date: string; billed: number; collected: number; outstanding: number; invoiceCount: number; paymentCount: number }>;
  totals: { billed: number; collected: number; outstanding: number; invoiceCount: number; paymentCount: number };
  byType: Record<string, number>;
  medicalAidTotal: number;
  privateTotal: number;
}

export function ReportsPage() {
  const { get, getBlob } = useApi();
  const [year, setYear] = useState(new Date().getFullYear());
  const [month, setMonth] = useState(new Date().getMonth() + 1);
  const [data, setData] = useState<MonthlyData | null>(null);
  const [loading, setLoading] = useState(true);
  const [exporting, setExporting] = useState(false);
  const toast = useToast(5000);

  const fetchReport = useCallback(async () => {
    setLoading(true);
    try {
      const result = await get<MonthlyData>(`/reports/monthly-revenue?year=${year}&month=${month}`);
      if (result.success && result.data) setData(result.data);
    } finally {
      setLoading(false);
    }
  }, [get, year, month]);

  useEffect(() => { fetchReport(); }, [fetchReport]);

  const handleExcelExport = useCallback(async () => {
    setExporting(true);
    toast.dismiss();
    try {
      const blob = await getBlob(`/reports/excel-export?year=${year}&month=${month}`);
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `IKhanGynae_Report_${year}-${String(month).padStart(2, '0')}.xlsx`;
      a.click();
      URL.revokeObjectURL(url);
    } catch {
      toast.show('Excel export failed. Please try again.', 'error');
    } finally {
      setExporting(false);
    }
  }, [year, month]);

  const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

  return (
    <main className="max-w-7xl mx-auto px-4 py-6">
      <div className="flex items-center justify-between mb-6">
        <h1 className="text-xl font-bold text-gray-900 flex items-center gap-2">
          <BarChart3 className="h-5 w-5" /> Reports
        </h1>
        <div className="flex items-center gap-3">
          <select value={month} onChange={e => setMonth(+e.target.value)} className="px-3 py-2 border rounded text-sm">
            {MONTHS.map((m, i) => <option key={i} value={i + 1}>{m}</option>)}
          </select>
          <select value={year} onChange={e => setYear(+e.target.value)} className="px-3 py-2 border rounded text-sm">
            {[2024, 2025, 2026, 2027].map(y => <option key={y} value={y}>{y}</option>)}
          </select>
          <button onClick={handleExcelExport} disabled={exporting}
            className="px-3 py-2 text-sm font-medium text-gray-700 bg-white border rounded hover:bg-gray-50 disabled:opacity-50 flex items-center gap-1.5">
            {exporting ? <Loader2 className="h-4 w-4 animate-spin" /> : <Download className="h-4 w-4" />} Excel Export
          </button>
        </div>
      </div>

      {toast.message && (
        <div className={`mb-4 p-3 rounded border text-sm flex items-center gap-2 ${toast.type === 'error' ? 'bg-red-50 border-red-200 text-red-700' : 'bg-green-50 border-green-200 text-green-700'}`}>
          {toast.type === 'error' && <AlertCircle className="h-4 w-4 flex-shrink-0" />}
          {toast.message}
        </div>
      )}

      {loading ? (
        <div className="flex justify-center py-20"><Loader2 className="h-8 w-8 animate-spin text-blue-600" /></div>
      ) : data ? (
        <div className="space-y-6">
          {/* Summary cards */}
          <div className="grid grid-cols-5 gap-4">
            <div className="bg-white rounded-lg shadow-sm border p-4">
              <p className="text-xs text-gray-500">Billed</p>
              <p className="text-xl font-bold text-gray-900">R {data.totals.billed.toFixed(2)}</p>
            </div>
            <div className="bg-white rounded-lg shadow-sm border p-4">
              <p className="text-xs text-gray-500">Collected</p>
              <p className="text-xl font-bold text-green-700">R {data.totals.collected.toFixed(2)}</p>
            </div>
            <div className="bg-white rounded-lg shadow-sm border p-4">
              <p className="text-xs text-gray-500">Outstanding</p>
              <p className={`text-xl font-bold ${data.totals.outstanding > 0 ? 'text-orange-600' : 'text-gray-900'}`}>R {data.totals.outstanding.toFixed(2)}</p>
            </div>
            <div className="bg-white rounded-lg shadow-sm border p-4">
              <p className="text-xs text-gray-500">Medical Aid</p>
              <p className="text-xl font-bold text-blue-700">R {data.medicalAidTotal.toFixed(2)}</p>
            </div>
            <div className="bg-white rounded-lg shadow-sm border p-4">
              <p className="text-xs text-gray-500">Private</p>
              <p className="text-xl font-bold text-purple-700">R {data.privateTotal.toFixed(2)}</p>
            </div>
          </div>

          {/* Revenue by type */}
          {Object.keys(data.byType).length > 0 && (
            <div className="bg-white rounded-lg shadow-sm border p-4">
              <h3 className="text-sm font-semibold text-gray-900 uppercase tracking-wider mb-3">Revenue by Type</h3>
              <div className="grid grid-cols-4 gap-3">
                {Object.entries(data.byType).map(([type, amount]) => (
                  <div key={type} className="p-3 rounded bg-gray-50 border">
                    <p className="text-xs text-gray-500 uppercase">{type.replace('_', ' ')}</p>
                    <p className="text-lg font-bold text-gray-900">R {amount.toFixed(2)}</p>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Daily breakdown */}
          <div className="bg-white rounded-lg shadow-sm border overflow-hidden">
            <div className="px-4 py-3 bg-gray-50 border-b">
              <h3 className="text-sm font-semibold text-gray-900 uppercase tracking-wider">Daily Breakdown</h3>
            </div>
            <table className="min-w-full divide-y divide-gray-200">
              <thead>
                <tr>
                  <th className="px-4 py-2 text-left text-xs font-medium text-gray-500">Date</th>
                  <th className="px-4 py-2 text-right text-xs font-medium text-gray-500">Billed</th>
                  <th className="px-4 py-2 text-right text-xs font-medium text-gray-500">Collected</th>
                  <th className="px-4 py-2 text-right text-xs font-medium text-gray-500">Outstanding</th>
                  <th className="px-4 py-2 text-right text-xs font-medium text-gray-500">Invoices</th>
                  <th className="px-4 py-2 text-right text-xs font-medium text-gray-500">Payments</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {data.days.map(day => (
                  <tr key={day.date}>
                    <td className="px-4 py-2 text-sm text-gray-900">{day.date}</td>
                    <td className="px-4 py-2 text-sm text-right">R {day.billed.toFixed(2)}</td>
                    <td className="px-4 py-2 text-sm text-right text-green-700">R {day.collected.toFixed(2)}</td>
                    <td className={`px-4 py-2 text-sm text-right ${day.outstanding > 0 ? 'text-orange-600' : 'text-gray-900'}`}>R {day.outstanding.toFixed(2)}</td>
                    <td className="px-4 py-2 text-sm text-right">{day.invoiceCount}</td>
                    <td className="px-4 py-2 text-sm text-right">{day.paymentCount}</td>
                  </tr>
                ))}
              </tbody>
              <tfoot className="bg-gray-50">
                <tr>
                  <td className="px-4 py-2 text-sm font-bold">TOTAL</td>
                  <td className="px-4 py-2 text-sm text-right font-bold">R {data.totals.billed.toFixed(2)}</td>
                  <td className="px-4 py-2 text-sm text-right font-bold text-green-700">R {data.totals.collected.toFixed(2)}</td>
                  <td className={`px-4 py-2 text-sm text-right font-bold ${data.totals.outstanding > 0 ? 'text-orange-600' : 'text-gray-900'}`}>R {data.totals.outstanding.toFixed(2)}</td>
                  <td className="px-4 py-2 text-sm text-right font-bold">{data.totals.invoiceCount}</td>
                  <td className="px-4 py-2 text-sm text-right font-bold">{data.totals.paymentCount}</td>
                </tr>
              </tfoot>
            </table>
          </div>
        </div>
      ) : (
        <p className="text-center text-gray-500">No data for this month.</p>
      )}
    </main>
  );
}
